import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Mountain, Mail, Shield, Clock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_CODE = "MINERAL_360"; // matriz comercial existente — pacote completo

export default function ConsultaMineralPublica() {
  const nav = useNavigate();
  const [processo, setProcesso] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!processo || !email) {
      toast.error("Informe o número do processo e o e-mail.");
      return;
    }
    setLoading(true);
    try {
      // 1) Quote (guest_email + criterio processo)
      const quoteRes = await axios.post(`${API}/api/mineral/orders/quote`, {
        guest_email: email.trim(),
        product_code: PRODUCT_CODE,
        quantity: 1,
        delivery_channel: "email",
        delivery_target: email.trim(),
        criteria: { processo: processo.trim() },
      });
      const orderId = quoteRes.data.order_id;
      // 2) Se veio como paid (usuário logado com crédito — improvável no público), pula pro relatório
      if (quoteRes.data.credit_used) {
        nav(`/consulta-mineral/aguardando?order=${orderId}`);
        return;
      }
      // 3) Cria checkout Stripe
      const checkoutRes = await axios.post(`${API}/api/mineral/orders/${orderId}/checkout`);
      window.location.href = checkoutRes.data.checkout_url;
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Falha ao criar consulta";
      toast.error(String(msg));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg, #050810 0%, #0A1730 40%, #0B1B2F 100%)" }}
         data-testid="consulta-mineral-publica">
      <div className="max-w-5xl mx-auto px-6 pt-10 pb-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition text-sm">
          <span aria-hidden="true">←</span> Voltar
        </Link>
        <div className="text-[10px] tracking-[.3em] uppercase font-semibold" style={{ color: "#F0D37A" }}>
          Hermes · Serviço público
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 pb-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-amber-400/30"
               style={{ background: "rgba(212, 175, 55, 0.08)", color: "#F0D37A" }}>
            <Mountain className="w-3.5 h-3.5"/>
            <span className="text-[11px] uppercase tracking-widest font-semibold">Consulta Mineral 360</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-white leading-tight">
            Descubra tudo sobre um <span style={{ color: "#D4AF37" }}>processo minerário</span>
          </h1>
          <p className="mt-4 text-neutral-300/80 max-w-xl mx-auto text-base leading-relaxed">
            Consulta jurídica automática sobre a base oficial da ANM · SIGMINE, com análise assinada pela LUNA — entrega em minutos, por e-mail.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl"
              data-testid="consulta-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="text-[11px] uppercase tracking-widest font-semibold text-white/60">
                Nº do processo minerário
              </label>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40"/>
                <input data-testid="input-processo" value={processo}
                  onChange={(e) => setProcesso(e.target.value)}
                  placeholder="ex: 880.954/1985 ou 880954/1985"
                  className="w-full h-12 pl-10 pr-4 rounded-xl bg-white/10 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-amber-400/60 transition"/>
              </div>
              <div className="text-[11px] text-white/40 mt-1.5">Fonte: dadosabertos.anm.gov.br/SIGMINE (base pública oficial)</div>
            </div>
            <div className="sm:col-span-2">
              <label className="text-[11px] uppercase tracking-widest font-semibold text-white/60">
                Seu e-mail (para receber o relatório)
              </label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40"/>
                <input data-testid="input-email" value={email} type="email"
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="advogado@escritorio.com"
                  className="w-full h-12 pl-10 pr-4 rounded-xl bg-white/10 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-amber-400/60 transition"/>
              </div>
            </div>
          </div>

          <button type="submit" disabled={loading} data-testid="btn-consultar"
            className="mt-6 w-full h-12 rounded-xl font-semibold text-base transition shadow-lg disabled:opacity-60"
            style={{ background: "linear-gradient(180deg, #F5DE8C 0%, #D4AF37 100%)", color: "#0B1B2F" }}>
            {loading ? "Aguarde…" : "Consultar processo →"}
          </button>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[11px]">
            <div className="flex items-start gap-2 text-white/60">
              <Shield className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "#D4AF37" }}/>
              <span>Nada é processado antes da confirmação real do pagamento.</span>
            </div>
            <div className="flex items-start gap-2 text-white/60">
              <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "#D4AF37" }}/>
              <span>Relatório disponível para download por 10 dias corridos.</span>
            </div>
            <div className="flex items-start gap-2 text-white/60">
              <Mountain className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "#D4AF37" }}/>
              <span>Base ANM oficial · situação, fase, titular, substância e riscos.</span>
            </div>
          </div>
        </form>

        <div className="text-center mt-6 space-x-4">
          <Link to="/consulta-mineral/lote" data-testid="link-consulta-lote"
                className="text-xs text-white/50 hover:text-white/80 transition">
            <span className="underline underline-offset-2">Consultar em lote (até 20)</span>
          </Link>
          <span className="text-white/20">·</span>
          <Link to="/consulta-mineral/meus" data-testid="link-meus-relatorios"
                className="text-xs text-white/50 hover:text-white/80 transition">
            Já comprou antes? <span className="underline underline-offset-2">Acessar meus relatórios</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
